/**
 * ProjectFieldsFieldset — the shared, rich project-detail field set.
 *
 * Used by BOTH the create-project modal (B-123) and the edit-details modal
 * (B-122) so the two surfaces can never drift. Presentational only: the caller
 * owns the value + onChange and decides create vs update.
 *
 * Per the founder's decision, a project has TWO identity fields: a Name (a
 * convenience label, e.g. "888 second st") AND the Insured/owner.
 */
import { Input } from './ui/input';
import { Select } from './ui/select';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { DAMAGE_TYPES } from '../../data/roomChecklists';
import type { StructuredAddress } from '../../types';

/** Editable project fields (all optional — partial edits are valid). */
export interface ProjectFieldValues {
  name?: string;
  owner_name?: string;
  owner_phone?: string;
  owner_email?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  claim_number?: string;
  policy_number?: string;
  date_of_loss?: string;
  date_of_inspection?: string;
  loss_type?: string;
}

function Field({
  label,
  k,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  k: keyof ProjectFieldValues;
  value: ProjectFieldValues;
  onChange: (patch: Partial<ProjectFieldValues>) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-[11px] font-[550] uppercase tracking-[.02em] text-[var(--tx-lo)]">
      {label}
      <Input
        type={type}
        value={value[k] ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange({ [k]: e.target.value })}
      />
    </label>
  );
}

/** The shared, scrollable project field set. */
export function ProjectFieldsFieldset({
  value,
  onChange,
}: {
  value: ProjectFieldValues;
  onChange: (patch: Partial<ProjectFieldValues>) => void;
}) {
  // Reason: Auto-fill street/city/state/zip from a Places selection. Only the
  // non-empty parts are patched, so the no-key fallback (which emits blank
  // city/state/zip) can never clobber values the user already typed.
  const handleAddressSelect = (addr: StructuredAddress) => {
    const patch: Partial<ProjectFieldValues> = {};
    if (addr.street) patch.street = addr.street;
    if (addr.city) patch.city = addr.city;
    if (addr.state) patch.state = addr.state;
    if (addr.zip) patch.zip = addr.zip;
    onChange(patch);
  };

  return (
    <div className="flex flex-col gap-3">
      <Field label="Project Name" k="name" value={value} onChange={onChange} placeholder="e.g. 888 second st" />
      <Field label="Insured" k="owner_name" value={value} onChange={onChange} placeholder="Owner / insured name" />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Phone" k="owner_phone" value={value} onChange={onChange} type="tel" />
        <Field label="Email" k="owner_email" value={value} onChange={onChange} type="email" />
      </div>
      <label className="flex flex-col gap-1.5 text-[11px] font-[550] uppercase tracking-[.02em] text-[var(--tx-lo)]">
        Quick address lookup
        <AddressAutocomplete
          onSelect={handleAddressSelect}
          placeholder="Start typing an address to auto-fill…"
          className="h-[34px] rounded-[var(--r-md)] bg-[var(--bg-inset)] border-[var(--bd)] text-[length:13px] text-[color:var(--tx-hi)] placeholder:text-[color:var(--tx-faint)] focus:ring-0 focus:border-[var(--accent-line)] focus:shadow-[0_0_0_3px_var(--accent-soft)]"
        />
      </label>
      <Field label="Street" k="street" value={value} onChange={onChange} />
      <div className="grid grid-cols-[1fr_70px_90px] gap-2">
        <Field label="City" k="city" value={value} onChange={onChange} />
        <Field label="State" k="state" value={value} onChange={onChange} />
        <Field label="ZIP" k="zip" value={value} onChange={onChange} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Claim #" k="claim_number" value={value} onChange={onChange} />
        <Field label="Policy #" k="policy_number" value={value} onChange={onChange} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Date of Loss" k="date_of_loss" value={value} onChange={onChange} type="date" />
        <Field label="Date of Inspection" k="date_of_inspection" value={value} onChange={onChange} type="date" />
      </div>
      <label className="flex flex-col gap-1.5 text-[11px] font-[550] uppercase tracking-[.02em] text-[var(--tx-lo)]">
        Loss Type
        <Select
          value={value.loss_type ?? ''}
          onChange={(e) => onChange({ loss_type: e.target.value })}
        >
          <option value="">Select loss type…</option>
          {/* Legacy free-text value not in the canonical list: keep it selectable
              so opening the edit modal can't silently drop it. */}
          {value.loss_type &&
            !DAMAGE_TYPES.some((d) => d.value === value.loss_type) && (
              <option value={value.loss_type}>{value.loss_type}</option>
            )}
          {DAMAGE_TYPES.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </Select>
      </label>
    </div>
  );
}
